/**
 * Tutor information-meeting form section.
 * Session picker unless already marked participated; checkbox stamps actor + timestamp.
 */
import * as React from 'react';
import { Info } from 'lucide-react';
import { Checkbox } from '@/components/forms/Checkbox';
import { DateTimePicker } from '@/components/forms/DateTimePicker';
import { MeetingSelector } from '@/components/common/MeetingSelector';
import {
  buildInformationMeetingParticipationPatch,
  shouldShowInformationMeetingParticipationFields,
} from '@/components/tutors/inperson/tutor-form-utils';

/** Props for {@link TutorInformationMeetingSection}. */
interface Props {
  mode: 'create' | 'edit';
  formData: Record<string, any>;
  tutorUserId: string | undefined;
  companyId: string;
  user: any;
  set: (field: string, value: any) => void;
  getFieldError?: (field: string) => string | undefined;
}

/** First calendar day of the current year (`YYYY-01-01`). */
const CURRENT_YEAR_START = `${new Date().getFullYear()}-01-01`;

/** Tutor information-meeting form section. */
export function TutorInformationMeetingSection({
  mode,
  formData,
  tutorUserId,
  companyId,
  user,
  set,
  getFieldError,
}: Readonly<Props>) {
  const showParticipationFields = shouldShowInformationMeetingParticipationFields(formData);

  function handleParticipatedChange(checked: boolean): void {
    const patch = buildInformationMeetingParticipationPatch(checked, formData, user);
    Object.entries(patch).forEach(([field, value]) => set(field, value));
  }

  function handleSessionIdChange(val: string | null): void {
    set('sessionId', val);
  }

  function handleParticipatedAtChange(val: string): void {
    set('participatedAt', val);
  }

  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <Info className="w-4 h-4" />
        Réunion d'information
      </h4>
      {!formData.hasParticipated ? (
        <MeetingSelector
          type="information_meeting_in_person"
          userId={tutorUserId ?? ''}
          companyId={companyId}
          value={formData.sessionId ?? null}
          onChange={handleSessionIdChange}
          mode={mode}
        />
      ) : (
        <p className="text-sm text-muted-foreground">
          Participation déjà effectuée — inscription à une séance non requise.
        </p>
      )}
      {showParticipationFields && (
        <div className="grid grid-cols-2 gap-4 items-end">
          <Checkbox
            label="A participé"
            checked={formData.hasParticipated === true}
            onCheckedChange={handleParticipatedChange}
            size="sm"
          />
          <DateTimePicker
            label="Date de participation"
            value={formData.participatedAt ?? ''}
            onChange={handleParticipatedAtChange}
            minDate={CURRENT_YEAR_START}
            disableFuture
            error={getFieldError?.('participatedAt')}
          />
        </div>
      )}
    </div>
  );
}
